import { orientationsFromConfig, orientationSummary, systemDesignForOrientation, updateOrientation } from "./orientations";
import { isRoofInstallation } from "./autoDesignLayout";
import { roofInitialGrid, fitRoofRowSpacing } from "../../utils/roofInitialGrid";
import { physicalLayoutCount, physicalLayoutDesignKey, layoutDesignChanged, updateLayoutFromDesign } from "../../utils/physicalLayoutDesign";
import { deriveField } from "../../utils/fieldGeometry";
import { mountingPlaneFor } from "../../utils/mounting";
import { descriptorRows } from "../../utils/projectScene";
import { legacyRoofModuleArray, moduleQuads, overlapCount } from "../../utils/moduleLayout";
import { modulePositions } from "../../utils/moduleShading";
import { buildTerrain } from "../../utils/sceneRows";
import { checkRoofRows, siteGeometryErrors } from "../../utils/siteGeometry";
import { sceneFormWithEdits } from "../../utils/sharedSceneEdits";

const savedSpacing = (cfg, plane) => plane.primary
  ? cfg.pv_design?.spacingInfo || cfg.pv_design?.spacing : plane.spacingInfo;

function descriptor(cfg, plane, spacing, count, { moduleLength, moduleWidth, portrait }) {
  const po = cfg.panelOrientation || {};
  const flush = po.installationType === "roof_flush";
  const mounting = flush ? { tilt: plane.tilt ?? 0, azimuth: plane.azim ?? 180 }
    : mountingPlaneFor(plane.primary ? cfg : { ...cfg, panelOrientation: {
    ...po, tilt: plane.tilt, azim: plane.azim, tiltReference: plane.tiltReference,
    roofPitch: plane.roofPitch ?? spacing.site_geometry?.roof?.pitchDeg ?? po.roofPitch,
    roofAzimuth: plane.roofAzimuth ?? spacing.site_geometry?.roof?.azimuthDeg ?? po.roofAzimuth,
  } }, spacing);
  const roof = spacing.site_geometry?.roof;
  return { id: plane.id, onRoof: true, spacing, totalModules: count,
    siteGeometry: roof && flush ? { ...spacing.site_geometry,
      roof: { ...roof, pitchDeg: mounting.tilt, azimuthDeg: mounting.azimuth } } : spacing.site_geometry,
    tilt: mounting.tilt, azimuth: mounting.azimuth,
    roofPitch: flush ? mounting.tilt : roof?.pitchDeg,
    roofAzimuth: flush ? mounting.azimuth : roof?.azimuthDeg,
    tiers: Number(spacing.row_vertical_modules) || 1,
    clearance: spacing.ground_clearance_m ?? plane.groundClearanceM ?? po.groundClearanceM ?? 0.5,
    moduleWidth: portrait === false ? moduleLength : moduleWidth,
    moduleAlongSlope: portrait === false ? moduleWidth : moduleLength,
    tables: spacing.parcel_tables || [],
  };
}

// Electrical save updates every affected face in ONE config snapshot. Existing
// working geometry wins over saved geometry; stale cached slot lists never do.
export async function syncRoofLayoutsAfterDesign(previous, next, options = {}) {
  const type = next.panelOrientation?.installationType;
  if (!isRoofInstallation(type)) return { config: next, changedIds: [], pendingIds: [] };
  const planes = orientationsFromConfig(next), oldPlanes = orientationsFromConfig(previous);
  const summary = orientationSummary(next), oldSummary = orientationSummary(previous);
  const updates = await Promise.all(planes.map(async (plane, i) => {
    const scoped = systemDesignForOrientation(next.systemDesign, planes, plane.id);
    const designKey = physicalLayoutDesignKey(scoped);
    const oldKey = physicalLayoutDesignKey(systemDesignForOrientation(previous.systemDesign, oldPlanes, plane.id));
    const count = summary[i].modules;
    const oldCount = oldSummary.find(p => p.id === plane.id)?.modules || 0;
    const saved = savedSpacing(next, plane);
    const draft = options.drafts?.[plane.id];
    const oldPlane = oldPlanes.find(p => p.id === plane.id);
    const compatible = draft?.source === JSON.stringify(oldPlane ? savedSpacing(previous, oldPlane) || null : null);
    const source = sceneFormWithEdits(compatible && draft.spacing ? draft.spacing : saved || {}, options.sceneEdits?.[plane.id]);
    const changed = oldKey !== designKey || oldCount !== count ||
      previous.read_module !== next.read_module || layoutDesignChanged(source, count, designKey) || options.preservePlanIds?.includes(plane.id);
    if (!changed) return null;
    const model = descriptor(next, plane, source, oldCount, options);
    // The saved spacing was a distance for the PREVIOUS module. Refit it to
    // the module now on the roof before any slot is built from it.
    const moduleChanged = previous.read_module !== next.read_module;
    const previousSpacing = compatible && draft?.spacing ? draft.spacing
      : oldPlane ? savedSpacing(previous, oldPlane) : null;
    const previousPitch = Number(previousSpacing?.minimum_distance_m ?? previousSpacing?.pitch_m ?? previousSpacing?.pitch);
    // A new auto-design pitch already belongs to the NEW module. Only refit
    // distances that actually existed for the old module.
    const previousAlongSlope = moduleChanged && previousPitch > 0
      ? (options.portrait === false ? options.previousModuleWidth : options.previousModuleLength) : null;
    const initial = fitRoofRowSpacing(roofInitialGrid(source, { ...options, installationType: type, tilt: model.tilt }) || {},
      { installationType: type, alongSlope: model.moduleAlongSlope, previousAlongSlope, tilt: model.tilt });
    const tiers = Number(initial.row_vertical_modules) || 1;
    const rows = initial.row_modules || deriveField({ totalModules: oldCount,
      pitch: Number(initial.minimum_distance_m) || 1, moduleWidth: Number(model.moduleWidth) || 1,
      verticalModules: tiers, fieldRows: initial.field_rows })?.rowModules || [];
    const baseRows = descriptorRows({ ...model, spacing: { ...initial, row_modules: rows,
      module_offsets: {}, module_array: null }, totalModules: physicalLayoutCount({ ...initial, row_modules: rows }) || oldCount });
    const base = baseRows.some(r => r.slot) ? baseRows : moduleQuads(baseRows, tiers);
    const terrain = buildTerrain(model.roofPitch, model.roofAzimuth);
    const array = initial.module_array || legacyRoofModuleArray(base, initial.module_offsets, terrain.groundZ);
    const sameGrid = physicalLayoutCount({ ...initial, row_modules: rows }) === count &&
      previous.read_module === next.read_module;
    const preservePlan = initial.manual_layout_edited || initial.excluded_slots?.length || options.preservePlanIds?.includes(plane.id);
    const rebuilt = sameGrid || preservePlan ? { ...initial, row_modules: rows, module_array: array,
      string_overrides: null, electrical_design_key: designKey }
      : updateLayoutFromDesign({ ...initial, row_modules: rows }, {
        ...options, totalModules: count, designKey, moduleArray: array,
      });
    const pitch = Number(rebuilt.minimum_distance_m);
    const spacing = { ...rebuilt, mode: "manual", minimum_distance_m: pitch, pitch_m: pitch,
      row_vertical_modules: Number(rebuilt.row_vertical_modules), field_rows: Number(rebuilt.field_rows) || null,
      fit_to_modules: count, module_positions: [], module_mapping_valid: false, scene_review_required: true,
      gcr: Number(model.moduleAlongSlope) * Number(rebuilt.row_vertical_modules) / pitch };
    // Replaced slot lists cannot retain a previous parcel fit or scene images.
    delete spacing.parcel_fit;
    delete spacing.fit_to_strings;
    delete spacing.string_columns;
    delete spacing.scene_views;
    let mapping;
    if (count && options.fetchMapping) {
      try {
        mapping = await options.fetchMapping({ systemDesign: scoped, rowModules: spacing.row_modules,
          tiers: spacing.row_vertical_modules, allowCrossRows: (spacing.string_routing || "continuous") === "continuous",
          excludedSlots: Array.isArray(spacing.excluded_slots) && spacing.excluded_slots.length ? spacing.excluded_slots : undefined });
        spacing.module_positions = modulePositions(mapping, spacing.row_vertical_modules);
      } catch {
        // Keep the electrical save and updated grid. The existing layout
        // validation reports pending mapping and retries when it is opened.
      }
    }
    const current = descriptor(next, plane, spacing, count, options);
    const geometry = current.siteGeometry;
    const moduleRows = descriptorRows(current);
    const quads = moduleRows.some(r => r.slot) ? moduleRows : moduleQuads(moduleRows, current.tiers);
    const status = checkRoofRows(geometry, quads);
    const placedCount = quads.length;
    const validGeometry = Boolean(geometry?.roof && geometry?.frame &&
      Number(options.moduleLength) > 0 && Number(options.moduleWidth) > 0 &&
      !siteGeometryErrors(geometry).length && placedCount === count &&
      !status.outside && !status.intersecting && !status.belowRoof && !overlapCount(quads));
    spacing.module_mapping_valid = validGeometry && (!count || Boolean(mapping?.check?.consistent && spacing.module_positions.length === count));
    if (preservePlan) {
      spacing.manual_layout_edited = true;
      if (mapping?.check?.consistent && spacing.module_positions.length === count && physicalLayoutCount(spacing) === count) {
        spacing.module_assignments = spacing.module_positions;
      }
    }
    return { plane, spacing, pending: !spacing.module_mapping_valid };
  }));
  let config = next;
  const changedIds = [], pendingIds = [];
  for (const update of updates.filter(Boolean)) {
    const { plane, spacing, pending } = update;
    const evidence = { spacingInfo: spacing, sceneViews: [],
      obstacleShading: { ...(plane.primary ? config.pv_design?.obstacleShading : plane.obstacleShading), stale: true } };
    config = plane.primary ? { ...config, pv_design: { ...config.pv_design, ...evidence } }
      : updateOrientation(config, plane.id, evidence);
    changedIds.push(plane.id);
    if (pending) pendingIds.push(plane.id);
  }
  // A changed receiver can also change the shadows on unchanged faces.
  if (changedIds.length) {
    config = { ...config, pv_design: { ...config.pv_design, sceneViews: [],
      obstacleShading: { ...config.pv_design?.obstacleShading, stale: true } } };
    for (const plane of planes.filter(p => !p.primary && !changedIds.includes(p.id))) {
      config = updateOrientation(config, plane.id, { sceneViews: [],
        obstacleShading: { ...plane.obstacleShading, stale: true } });
    }
  }
  for (const [i, plane] of orientationsFromConfig(config).entries()) {
    if (summary[i].modules > 0 && !savedSpacing(config, plane)?.module_mapping_valid && !pendingIds.includes(plane.id)) {
      pendingIds.push(plane.id);
    }
  }
  return { config, changedIds, pendingIds };
}
